import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Card from "components/card";
import { MdLink, MdCheckCircle, MdSettings } from 'react-icons/md';

// Integration logos
import salesforceLogo from '@assets/img/integrations/salesforce.png';
import blackbaudLogo from '@assets/img/integrations/blackbaud.png';
import handshakeLogo from '@assets/img/integrations/handshake.png';
import slateLogo from '@assets/img/integrations/slate.png';
import oracleLogo from '@assets/img/integrations/oracle.png';
import instructureLogo from '@assets/img/integrations/instructure.png';

// Salesforce OAuth Config
const SALESFORCE_CONFIG = {
  clientId: '3MVG97L7PWbPq6UxuLVxSLU98YVqgAJ8evWZkggx2UbL09L4TzCU4XqgOgg1Po_wrxCc2WBucVoU178TDLSUF',
  authUrl: 'https://login.salesforce.com/services/oauth2/authorize',
  redirectUri: `${window.location.origin}/admin/integrations`,
};

// PKCE Helper functions
const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  // Convert to base64url format
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

interface Integration {
  id: string;
  name: string;
  description: string;
  logo?: string;
  status: 'connected' | 'not_connected' | 'coming_soon';
}

interface IntegrationCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  integrations: Integration[];
}

const integrationCategories: IntegrationCategory[] = [
  {
    id: 'alumni',
    name: 'Alumni/Advancement',
    description: 'Connect with alumni management and fundraising platforms',
    color: 'blue',
    integrations: [
      {
        id: 'salesforce',
        name: 'Salesforce',
        description: 'Sync alumni data and engagement metrics with Salesforce CRM',
        logo: salesforceLogo,
        status: 'not_connected',
      },
      {
        id: 'blackbaud',
        name: 'Blackbaud Raiser\'s Edge',
        description: 'Import donor and constituent data from Raiser\'s Edge NXT',
        logo: blackbaudLogo,
        status: 'not_connected',
      },
    ],
  },
  {
    id: 'career',
    name: 'Career Services',
    description: 'Connect with career development and job placement platforms',
    color: 'green',
    integrations: [
      {
        id: 'handshake',
        name: 'Handshake',
        description: 'Sync student career profiles and employer connections',
        logo: handshakeLogo,
        status: 'not_connected',
      },
    ],
  },
  {
    id: 'enrollment',
    name: 'Enrollment/Admissions',
    description: 'Connect with enrollment and admissions management systems',
    color: 'purple',
    integrations: [
      {
        id: 'slate',
        name: 'Slate',
        description: 'Import prospective student and applicant data from Technolutions Slate',
        logo: slateLogo,
        status: 'not_connected',
      },
    ],
  },
  {
    id: 'sis',
    name: 'Student Information System (SIS)',
    description: 'Connect with student information and learning management systems',
    color: 'amber',
    integrations: [
      {
        id: 'peoplesoft',
        name: 'PeopleSoft',
        description: 'Sync student records and academic data from Oracle PeopleSoft',
        logo: oracleLogo,
        status: 'not_connected',
      },
      {
        id: 'instructure',
        name: 'Instructure (Canvas)',
        description: 'Import course enrollments and student data from Canvas LMS',
        logo: instructureLogo,
        status: 'not_connected',
      },
    ],
  },
];

const colorClasses: Record<string, { bg: string; border: string; text: string; icon: string }> = {
  blue: {
    bg: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30',
    border: 'border-blue-200 dark:border-blue-800/50',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    bg: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30',
    border: 'border-green-200 dark:border-green-800/50',
    text: 'text-green-700 dark:text-green-300',
    icon: 'text-green-600 dark:text-green-400',
  },
  purple: {
    bg: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30',
    border: 'border-purple-200 dark:border-purple-800/50',
    text: 'text-purple-700 dark:text-purple-300',
    icon: 'text-purple-600 dark:text-purple-400',
  },
  amber: {
    bg: 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/30',
    border: 'border-amber-200 dark:border-amber-800/50',
    text: 'text-amber-700 dark:text-amber-300',
    icon: 'text-amber-600 dark:text-amber-400',
  },
};

const Integrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [connectedIntegrations, setConnectedIntegrations] = useState<string[]>(() => {
    const saved = localStorage.getItem('connectedIntegrations');
    return saved ? JSON.parse(saved) : [];
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isConnecting, setIsConnecting] = useState(false);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state'); // integration id

    if (code && state) {
      // Check if we're in a popup window
      const isPopup = window.opener && window.opener !== window;

      if (isPopup) {
        // Send message to parent window and close popup
        window.opener.postMessage(
          { type: 'oauth_callback', code, state },
          window.location.origin
        );
        window.close();
        return;
      }

      // If not in popup, handle directly (fallback)
      console.log('OAuth callback received:', { code, state });

      const newConnected = [...connectedIntegrations, state];
      setConnectedIntegrations(newConnected);
      localStorage.setItem('connectedIntegrations', JSON.stringify(newConnected));
      localStorage.setItem(`${state}_auth_code`, code);

      setSearchParams({});
      setIsConnecting(false);
    }
  }, [searchParams, setSearchParams, connectedIntegrations]);

  // Listen for OAuth popup callback
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'oauth_callback') {
        const { code, state } = event.data;
        console.log('OAuth callback received from popup:', { code, state });

        const newConnected = [...connectedIntegrations, state];
        setConnectedIntegrations(newConnected);
        localStorage.setItem('connectedIntegrations', JSON.stringify(newConnected));
        localStorage.setItem(`${state}_auth_code`, code);

        setIsConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [connectedIntegrations]);

  const handleConnect = async (integrationId: string) => {
    setSelectedIntegration(integrationId);

    if (integrationId === 'salesforce') {
      setIsConnecting(true);

      // Generate PKCE code verifier and challenge
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store code verifier for token exchange later
      localStorage.setItem('salesforce_code_verifier', codeVerifier);

      // Build Salesforce OAuth URL with PKCE
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: SALESFORCE_CONFIG.clientId,
        redirect_uri: SALESFORCE_CONFIG.redirectUri,
        state: integrationId,
        scope: 'api refresh_token',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });

      const authUrl = `${SALESFORCE_CONFIG.authUrl}?${params.toString()}`;

      // Open popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        authUrl,
        'salesforce-oauth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,status=yes`
      );
    } else {
      console.log('Connecting to:', integrationId);
    }
  };

  const handleDisconnect = (integrationId: string) => {
    const newConnected = connectedIntegrations.filter(id => id !== integrationId);
    setConnectedIntegrations(newConnected);
    localStorage.setItem('connectedIntegrations', JSON.stringify(newConnected));
    localStorage.removeItem(`${integrationId}_auth_code`);
  };

  const handleConfigure = (integrationId: string) => {
    setSelectedIntegration(integrationId);
    console.log('Configuring:', integrationId);
  };

  const getIntegrationStatus = (integrationId: string): Integration['status'] => {
    if (connectedIntegrations.includes(integrationId)) {
      return 'connected';
    }
    return 'not_connected';
  };

  const getStatusBadge = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <MdCheckCircle className="h-3 w-3" />
            Connected
          </span>
        );
      case 'coming_soon':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            Coming Soon
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            Not Connected
          </span>
        );
    }
  };

  return (
    <div className="mt-8 w-full min-h-0">
      {/* Integration Categories */}
      <div className="space-y-8">
        {integrationCategories.map((category) => {
          const colors = colorClasses[category.color];

          return (
            <div key={category.id}>
              {/* Category Header */}
              <div className={`${colors.bg} ${colors.border} border rounded-2xl p-4 mb-4`}>
                <h2 className={`text-lg font-bold ${colors.text}`}>{category.name}</h2>
                <p className={`text-sm ${colors.text} opacity-80`}>{category.description}</p>
              </div>

              {/* Integration Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {category.integrations.map((integration) => {
                  const status = getIntegrationStatus(integration.id);

                  return (
                    <Card
                      key={integration.id}
                      extra="flex flex-col bg-white w-full rounded-2xl p-5 dark:!bg-navy-800 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${colors.bg} ${colors.border} border flex items-center justify-center w-12 h-12`}>
                            {integration.logo ? (
                              <img
                                src={integration.logo}
                                alt={`${integration.name} logo`}
                                className="h-8 w-8 object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <MdLink className={`h-5 w-5 ${colors.icon} ${integration.logo ? 'hidden' : ''}`} />
                          </div>
                          <div>
                            <h3 className="font-bold text-navy-700 dark:text-white">
                              {integration.name}
                            </h3>
                          </div>
                        </div>
                        {getStatusBadge(status)}
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                        {integration.description}
                      </p>

                      <div className="flex gap-2">
                        {status === 'connected' ? (
                          <>
                            <button
                              onClick={() => handleConfigure(integration.id)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 transition-colors"
                            >
                              <MdSettings className="h-4 w-4" />
                              Configure
                            </button>
                            <button
                              onClick={() => handleDisconnect(integration.id)}
                              className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                            >
                              Disconnect
                            </button>
                          </>
                        ) : status === 'coming_soon' ? (
                          <button
                            disabled
                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed dark:bg-navy-700 dark:text-gray-500"
                          >
                            Coming Soon
                          </button>
                        ) : (
                          <button
                            onClick={() => handleConnect(integration.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors"
                          >
                            <MdLink className="h-4 w-4" />
                            Connect
                          </button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Integrations;
